import clx from 'classnames';
import { type ReactNode } from 'react';
import styled from 'styled-components';

export type IndicatorPosition = 'start' | 'end';

export interface ICardOptionLayoutProps {
  indicatorPosition: IndicatorPosition;
  selected: boolean;
  disabled: boolean;
  readOnly: boolean;
  className?: string;
  children: ReactNode;
}

const CardOptionWrapper = styled.div`
  border: 2px solid ${({ theme }) => theme.colorBorderSecondary};
  background-color: ${({ theme }) => theme.colorBgContainer};
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  /*
   * Label Ant Design: prende tutta la card.
   * !important necessario per sovrascrivere la regola globale
   * .form-handler .ant-form-item .ant-radio-wrapper { margin-top: 10px }
   * definita in LayoutGlobalStyle.tsx — ha specificità più alta.
   * Se servono altri override valutare un approccio con id o scope più forte.
   */
  > label {
    width: 100%;
    flex: 1;
    margin-top: 0 !important;
    margin-inline-end: 0;
    padding: 16px;
    display: flex;
    align-items: flex-start;

    /* Indicatore radio/checkbox: fisso in alto */
    > .ant-radio,
    > .ant-checkbox {
      flex-shrink: 0;
      align-self: flex-start;
      margin-top: 2px;
    }

    /*
     * Override padding Ant Design (.ant-radio-wrapper span.ant-radio+*).
     * Indicatore a sinistra (default): spazio dopo il radio.
     * Indicatore a destra (indicator-end): spazio prima del radio.
     */
    > .ant-radio + *,
    > .ant-checkbox + * {
      padding-inline-start: 10px;
      padding-inline-end: 0;
    }

    /* Contenuto testuale: fisso in alto */
    > span:not(.ant-radio):not(.ant-checkbox) {
      align-self: flex-start;
    }

    /* Contenuto card */
    > *:last-child {
      flex: 1;
      min-width: 0;
    }
  }

  /* Indicatore a destra */
  &.indicator-end > label {
    > .ant-radio,
    > .ant-checkbox {
      order: 2;
      margin-inline-start: auto;
      margin-inline-end: 0;
    }

    > .ant-radio + *,
    > .ant-checkbox + * {
      padding-inline-start: 0;
      padding-inline-end: 10px;
    }

    > *:last-child {
      order: 1;
    }
  }

  &.selected {
    border-color: ${({ theme }) => theme.colorText};
  }

  /* Stato errore/warning validazione */
  .ant-form-item-has-error & {
    border-color: ${({ theme }) => theme.colorError};
  }

  .ant-form-item-has-warning & {
    border-color: ${({ theme }) => theme.colorWarning};
  }

  &:hover:not(.disabled):not(.read-only) {
    border-color: ${({ theme }) => theme.colorText};
  }

  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.read-only {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * Styled wrapper per la singola card di un radio/checkbox group.
 *
 * Gestisce: bordo, stati (selected, disabled, readOnly, error, warning),
 * posizione dell'indicatore (start/end) e override degli stili Ant Design
 * sulla label interna.
 *
 * Riutilizzabile per RadioCardGroup e futuro CheckboxCardGroup.
 */
export function CardOptionLayout({
  indicatorPosition,
  selected,
  disabled,
  readOnly,
  className,
  children,
}: Readonly<ICardOptionLayoutProps>) {
  return (
    <CardOptionWrapper
      className={clx('card-option', className, {
        selected,
        disabled,
        'read-only': readOnly,
        'indicator-end': indicatorPosition === 'end',
      })}
    >
      {children}
    </CardOptionWrapper>
  );
}
